import { ApiClient } from "@kansai-ai-hub/api-client";

import { queryClient } from "./query-client";
import { authQueryOptions } from "@/features/auth/auth-queries";
import { router } from "./router";

const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

/**
 * 実際に `/auth/refresh` を叩く。**直接呼ばないこと**（`refreshAccessToken` を使う）。
 */
async function requestRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "x-request-id": crypto.randomUUID() },
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** 飛行中の refresh。**アプリ全体で 1 本に束ねる。** */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * アクセストークン期限切れ時のサイレント更新。
 *
 * `ApiClient` を通せない配信（画像・添付の Blob 取得）でも同じ更新を使えるよう export する
 * （`lib/fetch-blob.ts`）。ここが 2 実装に割れると、片方だけトークン更新に乗らなくなる。
 *
 * **飛行中の 1 本に束ねるのが要点。** `ApiClient` は内部で single-flight を持っているが、
 * それは ApiClient 経由の呼び出しどうしでしか共有されない。Blob 取得が素で叩くと、
 * **同じ refresh Cookie を提示する 2 本目**が飛ぶ。サーバーはトークンをローテーションし、
 * 使用済みトークンの再提示を「盗用」とみなして**そのユーザーの全セッションを失効させる**
 * （`auth.service.ts` の reuse detection）。事務局が全デバイスで強制ログアウトされる。
 *
 * 実際に起きる並び: タブ復帰 → 未読数とサマリの再取得が 401 →（ApiClient の 1 本目）
 * → ほぼ同時に添付プレビューが 401 →（束ねていないと 2 本目）。
 */
export function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= requestRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// 認証キャッシュをクリアし、現在ルートの beforeLoad を再評価させるだけに留める。
// 遷移そのものは各ルートの beforeLoad に委ねる（router.invalidate）:
//   - 保護ルート(_authenticated) → user=null を検知して /?login=true&redirect=現在地 へ
//   - /                         → リダイレクト条件が無いのでその場に留まる
// ここから直接 navigate('/') すると、ランディングでのリンク preload(intent) 由来の
// 認証プローブ 401 でも / へ弾かれる、データ取得 401 と beforeLoad で二重遷移する、
// といった誤遷移・ちらつきを招くため行わない。
export function handleUnauthorized(): void {
  queryClient.setQueryData(authQueryOptions.queryKey, null);
  void router.invalidate();
}

export const apiClient = new ApiClient({
  baseUrl,
  // アクセストークン期限切れ時のサイレント更新。リフレッシュトークン Cookie で
  // /auth/refresh を叩き、成功すれば元リクエストが自動リトライされる。
  refresh: refreshAccessToken,
  onUnauthorized: handleUnauthorized,
});
